import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import { BETA_PROGRAM, DeployProgram } from '../../config/deployProgram';
import { BetaTestReport, DeployTest, DeployTestQuestion } from '../../types';
import { firmwareTypeLabel } from './deployTestUtils';

const formatDate = (value?: string | Date) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

interface BetaTakeTestPageProps {
  program?: DeployProgram;
}

type AnswerDrafts = Record<string, { value: string; additionalInfo: string }>;

const draftsFromReport = (report?: BetaTestReport | null): AnswerDrafts => {
  const drafts: AnswerDrafts = {};
  (report?.questionResponses || []).forEach((entry) => {
    drafts[String(entry.questionId)] = {
      value: entry.value || '',
      additionalInfo: entry.additionalInfo || '',
    };
  });
  return drafts;
};

const BetaTakeTestPage: React.FC<BetaTakeTestPageProps> = ({ program = BETA_PROGRAM }) => {
  const { testId } = useParams<{ testId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const phoneDeviceId = searchParams.get('phoneDeviceId') || '';
  const [test, setTest] = useState<DeployTest | null>(null);
  const [report, setReport] = useState<BetaTestReport | null>(null);
  const [answerDrafts, setAnswerDrafts] = useState<AnswerDrafts>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [completing, setCompleting] = useState(false);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const saveTimer = useRef<number | null>(null);
  const latestPayload = useRef<{
    taskResponses: BetaTestReport['taskResponses'];
    questionResponses: BetaTestReport['questionResponses'];
  } | null>(null);

  const taskDone = useMemo(() => {
    const map = new Map<string, boolean>();
    (report?.taskResponses || []).forEach((entry) => {
      map.set(String(entry.itemId), entry.done);
    });
    return map;
  }, [report]);

  const answers = useMemo(() => {
    const map = new Map<string, { value: string; additionalInfo: string }>();
    Object.entries(answerDrafts).forEach(([questionId, draft]) => {
      map.set(questionId, draft);
    });
    return map;
  }, [answerDrafts]);

  const buildPayload = useCallback((
    nextTest: DeployTest,
    nextReport: BetaTestReport,
    patch?: {
      itemId?: string;
      done?: boolean;
    },
    drafts: AnswerDrafts = answerDrafts
  ) => {
    const taskResponses = (nextTest.items || []).map((item) => ({
      itemId: String(item._id),
      done: patch?.itemId === String(item._id)
        ? Boolean(patch.done)
        : Boolean(nextReport.taskResponses.find((entry) => String(entry.itemId) === String(item._id))?.done),
    }));
    const questionResponses = (nextTest.questions || []).map((question) => {
      const draft = question._id ? drafts[String(question._id)] : null;
      return {
        questionId: String(question._id),
        value: draft?.value || '',
        additionalInfo: draft?.additionalInfo || '',
      };
    });
    return { taskResponses, questionResponses };
  }, [answerDrafts]);

  const persist = useCallback(async (reportId: string, payload: {
    taskResponses: BetaTestReport['taskResponses'];
    questionResponses: BetaTestReport['questionResponses'];
  }) => {
    setSaveState('saving');
    try {
      const response = await axios.patch(
        `${API_BASE_URL}${program.reportsApi}/${reportId}`,
        payload,
        { withCredentials: true },
      );
      if (response.data.report) {
        // Ignore stale autosave responses so text inputs don't jump while typing.
        if (latestPayload.current === payload) {
          setReport(response.data.report);
        }
      }
      setSaveState('saved');
    } catch (err) {
      const message =
        axios.isAxiosError(err) && err.response?.data?.message
          ? String(err.response.data.message)
          : 'Could not save your answers.';
      setError(message);
      setSaveState('idle');
    }
  }, [program.reportsApi]);

  const queueSave = useCallback((nextReport: BetaTestReport, payload: {
    taskResponses: BetaTestReport['taskResponses'];
    questionResponses: BetaTestReport['questionResponses'];
  }) => {
    latestPayload.current = payload;
    if (saveTimer.current) {
      window.clearTimeout(saveTimer.current);
    }
    saveTimer.current = window.setTimeout(() => {
      persist(nextReport._id, payload);
    }, 500);
  }, [persist]);

  useEffect(() => () => {
    if (saveTimer.current) {
      window.clearTimeout(saveTimer.current);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!testId) {
        setError('Test not found');
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const existing = await axios.get(
          `${API_BASE_URL}${program.reportsApi}/for-test/${testId}`,
          {
            withCredentials: true,
            params: phoneDeviceId ? { phoneDeviceId } : undefined,
          },
        );
        if (cancelled) return;
        const loadedTest = existing.data.test as DeployTest | null;
        let loadedReport = existing.data.report as BetaTestReport | null;
        if (!loadedTest) {
          setError('Test not found');
          setLoading(false);
          return;
        }
        if (loadedTest.status === 'testing' && (!loadedReport || loadedReport.awaitingStart)) {
          const started = await axios.post(
            `${API_BASE_URL}${program.reportsApi}`,
            { deployTestId: loadedTest._id, phoneDeviceId },
            { withCredentials: true },
          );
          if (cancelled) return;
          loadedReport = started.data.report;
        }
        setTest(loadedTest);
        setReport(loadedReport);
        setAnswerDrafts(draftsFromReport(loadedReport));
        setError(!loadedReport ? 'This test is not currently open.' : '');
        if (loadedTest.name) {
          document.title = `${program.brand} - ${loadedTest.name}`;
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            axios.isAxiosError(err) && err.response?.data?.message
              ? String(err.response.data.message)
              : 'Could not load this test.'
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [testId, phoneDeviceId, program.brand, program.reportsApi]);

  const updateTask = (itemId: string, done: boolean) => {
    if (!test || !report || report.status === 'completed') return;
    const payload = buildPayload(test, report, { itemId, done });
    setReport({ ...report, ...payload });
    queueSave(report, payload);
  };

  const updateQuestion = (question: DeployTestQuestion, patch: { value?: string; additionalInfo?: string }) => {
    if (!test || !report || report.status === 'completed' || !question._id) return;
    const questionId = String(question._id);
    const nextDrafts = {
      ...answerDrafts,
      [questionId]: {
        value: patch.value !== undefined ? patch.value : (answerDrafts[questionId]?.value || ''),
        additionalInfo: patch.additionalInfo !== undefined
          ? patch.additionalInfo
          : (answerDrafts[questionId]?.additionalInfo || ''),
      },
    };
    setAnswerDrafts(nextDrafts);
    const payload = buildPayload(test, report, undefined, nextDrafts);
    queueSave(report, payload);
  };

  const handleComplete = async () => {
    if (!report || completing || report.status === 'completed') return;
    if (saveTimer.current) {
      window.clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    setCompleting(true);
    setError('');
    try {
      const payload = latestPayload.current || (test ? buildPayload(test, report) : {
        taskResponses: report.taskResponses,
        questionResponses: report.questionResponses,
      });
      await axios.post(
        `${API_BASE_URL}${program.reportsApi}/${report._id}/complete`,
        payload,
        { withCredentials: true },
      );
      navigate('/', { replace: true });
    } catch (err) {
      const message =
        axios.isAxiosError(err) && err.response?.data?.message
          ? String(err.response.data.message)
          : 'Could not complete the test.';
      setError(message);
      setCompleting(false);
    }
  };

  const readOnly = report?.status === 'completed' || report?.status === 'rejected' || test?.status !== 'testing';
  const firmwareLabel = firmwareTypeLabel(test?.firmwareType);

  return (
    <main className="beta-take-page">
      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-light" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : !test ? (
        <div className="beta-take-card">
          <h1>Test not found</h1>
          <p className="text-muted">{error || 'This test does not exist.'}</p>
          <Link to="/" className="btn btn-secondary">Back home</Link>
        </div>
      ) : (
        <div className="beta-take-card">
          <div className="beta-take-kicker">
            {program.takeKicker}
            {report?.round ? ` · Round ${report.round}` : ''}
          </div>
          <h1>{test.name}</h1>
          <div className="beta-take-meta">
            {test.phoneDevice && (
              <div>
                <span>Phone</span>
                <strong>{test.phoneDevice.name} ({test.phoneDevice.platform.toUpperCase()})</strong>
              </div>
            )}
            <div>
              <span>Firmware</span>
              <strong>
                {test.firmwareVersion ? `v${test.firmwareVersion}` : 'Not selected'}
                {firmwareLabel ? ` (${firmwareLabel})` : ''}
              </strong>
            </div>
            <div>
              <span>Mobile App (iOS)</span>
              <strong>
                {test.phoneDevice?.platform === 'android'
                  ? 'Not required'
                  : test.mobileAppVersionIos || test.mobileAppVersion ? `v${test.mobileAppVersionIos || test.mobileAppVersion}` : 'Not selected'}
              </strong>
            </div>
            <div>
              <span>Mobile App (Android)</span>
              <strong>
                {test.phoneDevice?.platform === 'ios'
                  ? 'Not required'
                  : test.mobileAppVersionAndroid || test.mobileAppVersion ? `v${test.mobileAppVersionAndroid || test.mobileAppVersion}` : 'Not selected'}
              </strong>
            </div>
            <div>
              <span>Created</span>
              <strong>{formatDate(test.createdAt)}</strong>
            </div>
            <div>
              <span>Round</span>
              <strong>{report?.round || 1}</strong>
            </div>
          </div>

          {test.overview?.trim() && (
            <section className="beta-take-section">
              <h2>Instructions</h2>
              <p className="beta-take-body">{test.overview}</p>
            </section>
          )}

          <section className="beta-take-section">
            <h2>Tasks</h2>
            {(test.items || []).length === 0 ? (
              <p className="text-muted mb-0">No tasks for this test.</p>
            ) : (
              <ul className="beta-take-tasks">
                {(test.items || []).map((item) => (
                  <li key={item._id}>
                    <label>
                      <input
                        type="checkbox"
                        checked={Boolean(item._id && taskDone.get(String(item._id)))}
                        onChange={(event) => item._id && updateTask(String(item._id), event.target.checked)}
                        disabled={readOnly}
                      />
                      <span>
                        <strong>{item.text}</strong>
                        {item.description?.trim() && <small>{item.description}</small>}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="beta-take-section">
            <h2>Questions</h2>
            {(test.questions || []).length === 0 ? (
              <p className="text-muted mb-0">No questions for this test.</p>
            ) : (
              <div className="beta-take-questions">
                {(test.questions || []).map((question, index) => {
                  const answer = question._id
                    ? answers.get(String(question._id)) || { value: '', additionalInfo: '' }
                    : { value: '', additionalInfo: '' };
                  return (
                    <div key={question._id || index} className="beta-take-question">
                      <label className="fw-bold">{index + 1}. {question.prompt}</label>
                      {question.type === 'text' && (
                        <textarea
                          className="form-control"
                          rows={3}
                          value={answer.value}
                          onChange={(event) => updateQuestion(question, { value: event.target.value })}
                          disabled={readOnly}
                        />
                      )}
                      {question.type === 'boolean' && (
                        <div className="beta-take-choices">
                          {['yes', 'no'].map((option) => (
                            <label key={option} className="beta-take-choice">
                              <input
                                type="radio"
                                name={`question-${question._id}`}
                                checked={answer.value === option}
                                onChange={() => updateQuestion(question, { value: option })}
                                disabled={readOnly}
                              />
                              <span>{option === 'yes' ? 'Yes' : 'No'}</span>
                            </label>
                          ))}
                        </div>
                      )}
                      {question.type === 'choice' && (
                        <div className="beta-take-choices">
                          {(question.choices || []).filter(Boolean).map((choice) => (
                            <label key={choice} className="beta-take-choice">
                              <input
                                type="radio"
                                name={`question-${question._id}`}
                                checked={answer.value === choice}
                                onChange={() => updateQuestion(question, { value: choice })}
                                disabled={readOnly}
                              />
                              <span>{choice}</span>
                            </label>
                          ))}
                        </div>
                      )}
                      {question.type !== 'text' && (
                        <div className="beta-take-extra">
                          <label className="form-label fw-bold">
                            {question.additionalInfo?.trim() || 'Additional Info'}
                          </label>
                          <textarea
                            className="form-control"
                            rows={2}
                            value={answer.additionalInfo}
                            onChange={(event) => updateQuestion(question, { additionalInfo: event.target.value })}
                            disabled={readOnly}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          <div className="beta-take-footer">
            <div className="beta-take-save">
              {saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? 'Saved' : ''}
            </div>
            <div className="beta-take-actions">
              <Link to="/" className="btn btn-outline-secondary beta-take-back">Back</Link>
              {report && report.status !== 'completed' && report.status !== 'rejected' && test.status === 'testing' && (
                <button
                  type="button"
                  className="btn btn-primary beta-take-complete"
                  onClick={handleComplete}
                  disabled={completing}
                >
                  {completing ? 'Submitting…' : report.round && report.round > 1 ? `Complete Round ${report.round}` : 'Complete Test'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default BetaTakeTestPage;
